import { useRef } from 'react';
import { type CountryCode, type CountryCallingCode } from 'libphonenumber-js';

import {
    createDataProvider,
    createStaticDataFetcher,
    DataProvider,
    DataSection,
    DataSectionOfType,
    decorateWithResultProducer,
    StaticDataFetcherItem,
} from '@hh.ru/magritte-common-data-provider';
import { type LibPhoneNumberAPI } from '@hh.ru/magritte-ui-phone-input/useLibPhoneNumber';

const compareArrays = <T>(a: T[], b: T[]) => {
    if (a === b) {
        return true;
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0, j = a.length; i < j; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
};

export interface PhoneCodeOption extends StaticDataFetcherItem {
    value: CountryCode;
    callingCode: CountryCallingCode;
}
type PrepareOptionsArgs = {
    availableCountries?: CountryCode[];
    countryNames: Partial<Record<CountryCode, string>>;
    priorityCountriesTitle?: string;
    priorityCountries: CountryCode[];
    libPhoneNumberAPI: LibPhoneNumberAPI;
};

// нужен чтобы ссылка оставалась постоянной
const EMPTY_COUNTRIES_LIST: CountryCode[] = [] as const;
interface CountryOption extends StaticDataFetcherItem {
    text: string;
    searchKey: string;
    value: CountryCode;
    callingCode: CountryCallingCode;
}
interface CountryOptionWithDelimiter extends StaticDataFetcherItem {
    text: string;
    searchKey: string;
    value: CountryCode | string;
    callingCode?: CountryCallingCode;
    type?: 'delimiter';
}
export const useCountrySelectDataProvider = ({
    availableCountries,
    countryNames,
    priorityCountriesTitle,
    priorityCountries,
    libPhoneNumberAPI,
}: PrepareOptionsArgs): { dataProvider: DataProvider<PhoneCodeOption>; options: PhoneCodeOption[] } => {
    const { getCountryCallingCode, getCountries } = libPhoneNumberAPI;
    const dataProviderRef = useRef<DataProvider<PhoneCodeOption> | null>(null);
    const optionsRef = useRef<CountryOption[]>([]);
    const prevAvailableCountries = useRef<CountryCode[]>(availableCountries || EMPTY_COUNTRIES_LIST);
    const prevPriorityCountries = useRef<CountryCode[]>(priorityCountries || EMPTY_COUNTRIES_LIST);
    const prevApi = useRef<LibPhoneNumberAPI>(libPhoneNumberAPI);
    const priority = priorityCountries || EMPTY_COUNTRIES_LIST;

    // Мемоизация для бедных
    // Сравниваем непосредственно элементы массивов, потому что ссылка будет меняться при каждом ререндере
    // если в пропы передадут array literal
    // Ожидается что если эти пропы передаются, то содержащиеся в них массивы не должны
    // быть большого размера (до 10 элементов)
    if (dataProviderRef.current) {
        const propsHasChanged =
            !compareArrays(availableCountries || EMPTY_COUNTRIES_LIST, prevAvailableCountries.current) ||
            !compareArrays(priority, prevPriorityCountries.current) ||
            prevApi.current !== libPhoneNumberAPI;
        prevAvailableCountries.current = availableCountries || EMPTY_COUNTRIES_LIST;
        prevPriorityCountries.current = priority;
        prevApi.current = libPhoneNumberAPI;

        if (!propsHasChanged) {
            return { dataProvider: dataProviderRef.current, options: optionsRef.current };
        }
    }

    const countries = availableCountries ?? getCountries();
    const options = countries
        // убираем из списка страны для которых не передано название в переводах
        .filter((country) => !!countryNames[country])
        .map<CountryOption>((country) => ({
            text: countryNames[country] as string,
            value: country,
            searchKey: `+${getCountryCallingCode(country)}`,
            callingCode: getCountryCallingCode(country),
        }))
        // сортируем опции, если страна есть в приоритетном списке, то используем её индекс в этом списке
        // если нет, то сортируем по алфавиту
        .sort((a, b) => {
            const aIndex = priority.indexOf(a.value);
            const bIndex = priority.indexOf(b.value);
            if (aIndex === -1 && bIndex === -1) {
                return a.text.localeCompare(b.text);
            }

            return (
                (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
            );
        });

    optionsRef.current = options;

    const optionsWithDelimiter: CountryOptionWithDelimiter[] = [...options];

    // если опций больше 10, то добавляем разделители в виде букв
    if (optionsWithDelimiter.length > 10) {
        let offset = 0;
        // если есть список приоритетных стран, то считаем их количество, добавляем перед ними разделитель Популярные
        // добавляем к количеству 1, т.к. добавился еще разделитель
        if (priority.length > 0) {
            offset =
                priority.reduce((acc, country) => (countries.includes(country) ? acc + 1 : acc), 0) +
                (priorityCountriesTitle ? 1 : 0);
            if (priorityCountriesTitle) {
                optionsWithDelimiter.unshift({
                    text: priorityCountriesTitle,
                    searchKey: `delimiter`,
                    value: 'delimiter-popular',
                    type: 'delimiter',
                });
            }
        }
        let previousLetter = '';
        // проходимся по списку и добавляем разделители
        // для этого сравниваем первую букву названия страны с предыдущей
        // если она изменилась, значит нужно добавить разделитель в виде буквы
        for (let i = offset; i < optionsWithDelimiter.length; i++) {
            const letter = optionsWithDelimiter[i].text[0].toUpperCase();
            if (letter !== previousLetter) {
                optionsWithDelimiter.splice(i, 0, {
                    text: letter,
                    value: `delimiter-${letter}`,
                    type: 'delimiter',
                    searchKey: 'delimiter',
                });
                previousLetter = letter;
            }
        }
    }

    dataProviderRef.current = createDataProvider({
        minCharsCount: 0,
        debounceTimeout: 0,
        fetcher: decorateWithResultProducer(createStaticDataFetcher(optionsWithDelimiter, 'searchKey'), (data) => {
            const sections: DataSection<PhoneCodeOption>[] = [];
            let currentSection: DataSection<PhoneCodeOption> | null = null;
            data?.forEach((option) => {
                const optionType = option.type ?? 'cells';
                if (
                    !currentSection ||
                    optionType !== (currentSection as DataSection)?.type ||
                    optionType === 'delimiter'
                ) {
                    if (optionType === 'delimiter') {
                        currentSection = { type: optionType, text: option.text };
                    } else {
                        currentSection = {
                            type: 'cells',
                            items: [
                                {
                                    value: option.value,
                                    data: {
                                        ...option,
                                        value: option.value as CountryCode,
                                        callingCode: option.callingCode as CountryCallingCode,
                                    },
                                },
                            ],
                        };
                    }
                    sections.push(currentSection);
                } else {
                    (currentSection as DataSectionOfType<'cells', PhoneCodeOption>).items.push({
                        value: option.value,
                        data: {
                            ...option,
                            value: option.value as CountryCode,
                            callingCode: option.callingCode as CountryCallingCode,
                        },
                    });
                }
            });
            return sections;
        }),
    });

    return { dataProvider: dataProviderRef.current, options };
};
